import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Volume2, VolumeX } from 'lucide-react';

/**
 * DEO-Bot — sphere + bubble. Tüm boyutlar CSS clamp() ile responsive.
 *  - inner=false: dikey (Hero/Landing) + plaka
 *  - inner=true: yatay — balon SOLDA, plaka YOK (panel iç sayfalar)
 *  - sticky=true: bottom-right fixed mini bot (scroll sticky)
 *  - showPlaque kontrolü için plaque prop'u var (default: !inner)
 *  - TTS: OpenAI "echo" via /api/tts (energetic young male voice)
 */
export default function DeoBot({
  compact = false,
  sphereBoost = 1,
  inner = false,
  sticky = false,
  plaque,
}) {
  const showPlaque = plaque !== undefined ? plaque : !inner;
  const [speaking, setSpeaking] = useState(false);
  const audioRef = useRef(null);

  const bubbleText =
    'Bugün portföyündeki 3 ilan için 12 yeni potansiyel müşteri yakaladım, sunumlarını hazırlamamı ister misin?';

  // Komponent unmount olunca sesi durdur
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        try { audioRef.current.pause(); } catch (_) {}
      }
    };
  }, []);

  const speak = useCallback(async () => {
    // Konuşuyorsa → durdur
    if (speaking && audioRef.current) {
      try { audioRef.current.pause(); } catch (_) {}
      audioRef.current = null;
      setSpeaking(false);
      return;
    }
    try {
      setSpeaking(true);
      const backend = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backend}/api/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: bubbleText, voice: 'echo', speed: 1.08 }),
      });
      if (!res.ok) throw new Error(`TTS ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setSpeaking(false); URL.revokeObjectURL(url); };
      audio.onerror = () => { setSpeaking(false); URL.revokeObjectURL(url); };
      await audio.play();
    } catch (e) {
      console.error('TTS error', e);
      setSpeaking(false);
    }
  }, [speaking]);

  /* Responsive sphere size via CSS clamp() */
  let sphereCSS;
  if (sticky) {
    sphereCSS = '92px';
  } else if (compact) {
    sphereCSS = `clamp(110px, 22vw, ${Math.round(180 * sphereBoost)}px)`;
  } else if (inner) {
    sphereCSS = `clamp(120px, 18vw, ${Math.round(220 * sphereBoost)}px)`;
  } else {
    // hero / landing
    sphereCSS = `clamp(160px, 32vw, ${Math.round(360 * sphereBoost)}px)`;
  }

  const bubbleWidth = sticky ? 0 : inner ? 'clamp(180px, 22vw, 240px)' : compact ? '220px' : 'clamp(220px, 22vw, 260px)';

  // Sticky mini bot — sadece küre + tıklanınca balon
  if (sticky) {
    return (
      <StickyMiniBot speaking={speaking} onSpeak={speak} bubbleText={bubbleText} />
    );
  }

  const Sphere = (
    <div
      className="relative"
      style={{
        width: sphereCSS,
        height: sphereCSS,
        animation: inner ? 'bot-idle-soft 6s ease-in-out infinite' : 'bot-idle 7s ease-in-out infinite',
      }}
    >
      <div
        className="absolute inset-0 rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(197,108,57,0.18) 0%, transparent 65%)',
          filter: 'blur(14px)',
          transform: 'scale(1.15)',
        }}
      />
      <img
        src="/assets/brand/deo_bot_clean.png"
        alt="DEO Bot"
        className="relative w-full h-full object-contain"
        style={{
          mixBlendMode: 'lighten',
          filter: 'drop-shadow(0 14px 28px rgba(0,0,0,0.7))',
        }}
        draggable={false}
      />
      <span
        className="absolute pointer-events-none rounded-full"
        style={{
          top: '46%', left: '7%',
          width: `calc(${sphereCSS} * 0.04)`, height: `calc(${sphereCSS} * 0.04)`,
          transform: 'translateY(-50%)',
          background: '#68E5B2',
          animation: 'eye-glow-left 4.5s ease-in-out infinite',
        }}
      />
      <span
        className="absolute pointer-events-none rounded-full"
        style={{
          top: '46%', right: '7%',
          width: `calc(${sphereCSS} * 0.04)`, height: `calc(${sphereCSS} * 0.04)`,
          transform: 'translateY(-50%)',
          background: '#F7D002',
          animation: 'eye-glow-right 4.5s ease-in-out infinite',
        }}
      />
    </div>
  );

  const DeoLabel = (
    <div className="w-full flex justify-center" style={{ marginTop: `-2px` }} data-testid="deo-bot-label">
      <div className="font-display copper-shine font-bold tracking-[0.40em] text-[18px] sm:text-[22px] uppercase pl-[0.40em]">
        DEO
      </div>
    </div>
  );

  const Bubble = ({ arrow = 'top' }) => (
    <div className="relative" style={{ width: bubbleWidth }} data-testid="deo-bot-bubble">
      <div
        className="relative rounded-2xl px-4 py-3"
        style={{
          background: 'linear-gradient(135deg, rgba(15, 8, 4, 0.96), rgba(28, 19, 13, 0.92))',
          border: '1px solid rgba(197, 108, 57, 0.55)',
          boxShadow: '0 12px 32px -8px rgba(0,0,0,0.7), 0 1px 0 rgba(244, 213, 184, 0.2) inset',
        }}
      >
        {arrow === 'top' ? (
          <div className="absolute" style={{
            top: '-7px', right: '50%', transform: 'translateX(50%) rotate(45deg)',
            width: '12px', height: '12px',
            background: 'rgba(15, 8, 4, 0.96)',
            borderTop: '1px solid rgba(197, 108, 57, 0.55)',
            borderLeft: '1px solid rgba(197, 108, 57, 0.55)',
          }} />
        ) : (
          <div className="absolute" style={{
            right: '-7px', top: '32px', transform: 'rotate(45deg)',
            width: '12px', height: '12px',
            background: 'rgba(15, 8, 4, 0.96)',
            borderTop: '1px solid rgba(197, 108, 57, 0.55)',
            borderRight: '1px solid rgba(197, 108, 57, 0.55)',
          }} />
        )}

        <div className="font-body text-[11px] leading-[1.5] text-[#F4ECDF]/95">
          Bugün portföyündeki <span className="copper-shine font-semibold">3 ilan</span> için
          <span className="copper-shine font-semibold"> 12 yeni potansiyel müşteri </span>
          yakaladım, sunumlarını hazırlamamı ister misin?
        </div>
        <div className="flex items-center gap-1.5 mt-2.5 pt-2.5 border-t border-white/5 flex-wrap">
          <button className="capsule capsule-mint hover:opacity-90 transition-opacity text-[8.5px]" data-testid="bot-action-yes">
            Evet, hazırla
          </button>
          <button className="capsule capsule-copper hover:opacity-90 transition-opacity text-[8.5px]" data-testid="bot-action-detail">
            Detay göster
          </button>
          <button
            type="button"
            onClick={speak}
            className="ml-auto inline-flex items-center justify-center rounded-full transition-opacity hover:opacity-80"
            style={{
              width: '22px', height: '22px',
              background: speaking ? 'rgba(247, 208, 2, 0.32)' : 'rgba(247, 208, 2, 0.14)',
              border: '1px solid rgba(247, 208, 2, 0.45)',
              color: '#F7D002',
            }}
            title={speaking ? 'Sesi durdur' : 'Sesle dinle'}
            data-testid="bot-action-tts"
          >
            {speaking ? <VolumeX size={11} /> : <Volume2 size={11} />}
          </button>
        </div>
      </div>
    </div>
  );

  /* INNER MODE — yatay */
  if (inner) {
    return (
      <div className="flex items-start gap-3 sm:gap-4" data-testid="deo-bot">
        <div className="flex-shrink-0 mt-2">
          <Bubble arrow="right" />
        </div>
        <div className="flex flex-col items-center flex-shrink-0">
          {Sphere}
          {DeoLabel}
        </div>
      </div>
    );
  }

  /* DEFAULT — dikey */
  return (
    <div className="relative flex flex-col items-end" data-testid="deo-bot">
      {Sphere}
      {DeoLabel}

      <div className="mt-2">
        <Bubble arrow="top" />
      </div>

      {showPlaque && (
        <div className="mt-3" style={{ width: compact ? '160px' : '180px' }} data-testid="deo-bot-plaque">
          <div
            className="rounded-md px-3 py-2 relative"
            style={{
              background: `linear-gradient(135deg,
                #C8C5BE 0%, #9E9B95 22%, #6F6B65 45%, #4D4A45 55%,
                #6F6B65 65%, #9E9B95 78%, #C8C5BE 100%)`,
              border: '1px solid rgba(0, 0, 0, 0.55)',
              boxShadow: '0 6px 14px rgba(0,0,0,0.5), 0 1px 0 rgba(255,255,255,0.45) inset',
            }}
          >
            <span className="absolute top-1 left-1.5 w-1 h-1 rounded-full"
              style={{ background: 'radial-gradient(circle at 30% 30%, #2B2622, #0A0908)' }} />
            <span className="absolute top-1 right-1.5 w-1 h-1 rounded-full"
              style={{ background: 'radial-gradient(circle at 30% 30%, #2B2622, #0A0908)' }} />
            <span className="absolute bottom-1 left-1.5 w-1 h-1 rounded-full"
              style={{ background: 'radial-gradient(circle at 30% 30%, #2B2622, #0A0908)' }} />
            <span className="absolute bottom-1 right-1.5 w-1 h-1 rounded-full"
              style={{ background: 'radial-gradient(circle at 30% 30%, #2B2622, #0A0908)' }} />
            <div className="font-display text-[10px] tracking-[0.18em] text-[#1C130D] font-bold uppercase text-center leading-tight">
              DEO-BOT
            </div>
            <div className="h-px my-0.5 bg-[#1C130D]/40 mx-2"/>
            <div className="font-body text-[6px] tracking-[0.20em] text-[#1C130D]/85 text-center uppercase leading-tight">
              Fragmented AI<br/>Intelligence System
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* Sticky mini-bot — sağ üst köşede 3D küre görünümü (scroll'da görünür) */
function StickyMiniBot({ speaking, onSpeak, bubbleText }) {
  const [open, setOpen] = useState(false);
  const size = 96;
  return (
    <div
      className="fixed z-50"
      style={{ top: 'max(76px, env(safe-area-inset-top))', right: '16px' }}
      data-testid="sticky-deo-bot"
    >
      {open && (
        <div
          className="absolute rounded-2xl px-4 py-3"
          style={{
            top: `${size + 14}px`,
            right: '0',
            width: 'min(280px, calc(100vw - 32px))',
            background: 'linear-gradient(135deg, rgba(15, 8, 4, 0.96), rgba(28, 19, 13, 0.92))',
            border: '1px solid rgba(197, 108, 57, 0.55)',
            boxShadow: '0 18px 40px -8px rgba(0,0,0,0.6)',
          }}
        >
          <div className="absolute" style={{
            top: '-7px', right: '36px', transform: 'rotate(45deg)',
            width: '12px', height: '12px',
            background: 'rgba(15, 8, 4, 0.96)',
            borderTop: '1px solid rgba(197, 108, 57, 0.55)',
            borderLeft: '1px solid rgba(197, 108, 57, 0.55)',
          }} />
          <div className="font-body text-[9px] tracking-[0.22em] uppercase text-[#E08A4F] font-semibold mb-1">
            DEO-Bot
          </div>
          <div className="font-body text-[11px] leading-[1.5] text-[#F4ECDF]/95">
            {bubbleText}
          </div>
          <div className="flex items-center gap-1.5 mt-2.5 pt-2.5 border-t border-white/5">
            <button className="capsule capsule-mint text-[8.5px]">Evet, hazırla</button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onSpeak(); }}
              className="ml-auto inline-flex items-center justify-center rounded-full hover:opacity-80"
              style={{
                width: '22px', height: '22px',
                background: speaking ? 'rgba(247, 208, 2, 0.32)' : 'rgba(247, 208, 2, 0.14)',
                border: '1px solid rgba(247, 208, 2, 0.45)',
                color: '#F7D002',
              }}
              data-testid="sticky-bot-tts"
            >
              {speaking ? <VolumeX size={11} /> : <Volume2 size={11} />}
            </button>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen(o => !o)}
        className="relative transition-transform hover:scale-105"
        style={{
          width: `${size}px`,
          height: `${size}px`,
          animation: 'bot-idle-soft 6s ease-in-out infinite',
        }}
        title={open ? 'Kapat' : 'DEO-Bot ile konuş'}
        data-testid="sticky-bot-toggle"
      >
        {/* Bakır glow halo */}
        <div
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(197,108,57,0.32) 0%, transparent 65%)',
            filter: 'blur(12px)',
            transform: 'scale(1.18)',
          }}
        />
        {/* 3D küre — inline bot ile aynı blend modu */}
        <img
          src="/assets/brand/deo_bot_clean.png"
          alt="DEO Bot"
          className="relative w-full h-full object-contain"
          style={{
            mixBlendMode: 'lighten',
            filter: 'drop-shadow(0 10px 22px rgba(0,0,0,0.65))',
          }}
          draggable={false}
        />
        {/* Sol göz - yeşil */}
        <span className="absolute pointer-events-none rounded-full" style={{
          top: '46%', left: '7%',
          width: `${size * 0.04}px`, height: `${size * 0.04}px`,
          transform: 'translateY(-50%)',
          background: '#68E5B2',
          animation: 'eye-glow-left 4.5s ease-in-out infinite',
        }} />
        {/* Sağ göz - sarı */}
        <span className="absolute pointer-events-none rounded-full" style={{
          top: '46%', right: '7%',
          width: `${size * 0.04}px`, height: `${size * 0.04}px`,
          transform: 'translateY(-50%)',
          background: '#F7D002',
          animation: 'eye-glow-right 4.5s ease-in-out infinite',
        }} />
      </button>
    </div>
  );
}

export function DeoBotPlaque() { return null; }
